import { DefaultApi, FeatureFlag, KeyValue, UpsertArrowFlightSqlAppInfoRequest } from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Ajv, { JSONSchemaType } from "ajv";
import { useForm } from "antd/lib/form/Form";
import * as React from "react";
import { useSelector } from "react-redux";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { AppState } from "src/store";
import styled from "styled-components";

import { QueryCacheKey } from "../api";
import {
  Alert,
  AlertExclamation,
  Button,
  CaretRightBlack,
  FlexRow,
  Form,
  FormItem,
  ImportTypeIcon,
  Input,
  List,
  message,
  Modal,
  OAuthConfiguredAlert,
  SettingsLayout,
} from "../components";
import { COLORS } from "../constants";
import { useApiContext, useTrack } from "../utils";

const HelperText = styled.div`
  color: ${COLORS.black45};
  margin-bottom: 20px;
`;

const StyledAlert = styled(Alert)<{ $background?: string; $marginBottom?: string }>`
  line-height: 1.8em;
  padding: 12px;

  code {
    white-space: nowrap;
    background-color: ${COLORS.black4};
    padding: 4px 6px;
  }

  .ant-alert-icon {
    margin: 6px;
  }

  ${({ $background }) => ($background ? `background-color: ${$background};` : "")}
  ${({ $marginBottom }) => ($marginBottom ? `margin-bottom: ${$marginBottom};` : "")}
`;

const StyledModal = styled(Modal)`
  &.ant-modal {
    .ant-modal-body {
      padding: 5px;
    }
  }
`;

const CallHeaderRow = styled(FlexRow)`
  margin-bottom: 8px;
`;

const CallHeaderLabel = styled.div`
  font-weight: 500;
  margin-bottom: 8px;
`;

const RemoveHeaderButton = styled(Button)`
  min-width: 32px;
  padding: 4px 8px;
`;

const StyledLabel = styled.div`
  label {
    font-weight: bold;
  }

  margin: 6px 0;
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  margin: 8px 16px;
`;

const StyledSummaryText = styled.div`
  margin-right: 12px;
  color: ${COLORS.black45};
`;

const { Item } = List;

const DataSourceSelections = styled.div`
  border-radius: 6px;
  cursor: default !important;

  .ant-list-item {
    .actionswrap {
      visibility: visible !important;
      border-radius: 6px !important;
    }
  }
`;

const StyledItem = styled(Item)`
  && {
    background: ${COLORS.black2};
    border: none;
    cursor: pointer;
    position: relative;
    margin-bottom: 8px;

    &:hover,
    &:focus-within,
    &:focus,
    &:active {
      background-color: ${COLORS.black4};

      .actionswrap {
        visibility: visible;
      }
    }

    .actionswrap {
      visibility: hidden;
    }

    .ant-list-item {
      margin: 12px 15px !important;
      border: 10px;
      background-color: ${COLORS.black4} !important;
      border-radius: 6px !important;
      cursor: default !important;

      .actionswrap {
        visibility: visible !important;
        border-radius: 6px !important;
      }
    }

    .ant-list-item-meta {
      align-items: center;
    }

    .ant-list-item-meta-title {
      color: ${COLORS.black45};
      line-height: 1.25;

      h4 {
        margin: 0px;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
      }
    }

    .ant-list-item-meta-description {
      color: ${COLORS.black85};
      font-size: 14px;
      font-weight: bold;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
  }
`;

const ajv = new Ajv();
const schema: JSONSchemaType<UpsertArrowFlightSqlAppInfoRequest> = {
  type: "object",
  properties: {
    host: { type: "string" },
    port: { type: "integer" },
    client_id: { type: "string" },
    client_secret: { type: "string" },
    authorization_url: { type: "string" },
    token_url: { type: "string" },
    scopes: { type: "string", nullable: true },
    call_headers: {
      type: "array",
      nullable: true,
      items: {
        type: "object",
        properties: {
          key: { type: "string" },
          value: { type: "string" },
        },
        required: ["key", "value"],
      },
    },
    domain: { type: "string", nullable: true },
  },
  required: ["host", "port", "client_id", "client_secret", "authorization_url", "token_url"],
};

const validate = ajv.compile(schema);

const fetchArrowFlightSqlAppInfo = async (api: DefaultApi) => {
  const response = await api.listArrowFlightSqlAppInfo();
  return response?.data?.apps_list;
};

export const ArrowFlightSqlOAuthSettings = () => {
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [form] = useForm<UpsertArrowFlightSqlAppInfoRequest>();
  const [errorMessage, setErrorMessage] = React.useState("");
  const [callHeaders, setCallHeaders] = React.useState<Array<KeyValue>>([{ key: "", value: "" }]);

  const track = useTrack();
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();

  const searchParams = new URLSearchParams(location.search);
  const redirectOnAdd = searchParams.get("back_on_add");
  const isAddNewAccount = searchParams.get("addNewAccount") === "true";

  const [showAddNewAccountForm, setShowAddNewAccountForm] = React.useState(isAddNewAccount);

  const { value: featureEnabled } = useFeatureGate(FeatureFlag.EnableArrowFlightSqlConnector);
  const isEnabled = featureEnabled && !clientFlags?.is_gmail_like_domain;

  React.useEffect(() => {
    document.title = "Arrow Flight SQL OAuth Settings - Coefficient Workspace";
    track("workspace_arrow_flight_sql_oauth_settings_viewed");
  }, []);

  React.useEffect(() => {
    if (showAddNewAccountForm) {
      form.setFieldsValue({
        host: "",
        port: 443,
        client_id: "",
        client_secret: "",
        authorization_url: "",
        token_url: "",
        scopes: "",
      });
      setCallHeaders([{ key: "", value: "" }]);
    }
  }, [showAddNewAccountForm]);

  const { apiClient: api } = useApiContext();

  const queryClient = useQueryClient();

  const { data: appInfoList, isLoading } = useQuery(
    [QueryCacheKey.ARROW_FLIGHT_SQL_APP_INFO],
    () => fetchArrowFlightSqlAppInfo(api),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: isEnabled,
    }
  );

  const upsertAppInfoMutation = useMutation(
    (json: UpsertArrowFlightSqlAppInfoRequest) =>
      api.upsertArrowFlightSqlAppInfo({
        upsert_arrow_flight_sql_app_info_request: {
          host: json.host,
          client_id: json.client_id,
          client_secret: json.client_secret,
          authorization_url: json.authorization_url,
          token_url: json.token_url,
          scopes: json.scopes,
          port: json.port,
          call_headers:
            callHeaders.filter(h => h.key).length > 0 ? callHeaders.filter(h => h.key) : undefined,
        },
      }),
    {
      onSuccess: () => {
        message.success("Saved successfully");
        setShowSuccessModal(true);
        setShowAddNewAccountForm(false);
        form.resetFields();
        queryClient.invalidateQueries([QueryCacheKey.ARROW_FLIGHT_SQL_APP_INFO]);
        if (redirectOnAdd) {
          history.goBack();
        }
      },
    }
  );

  if (!isEnabled) {
    return (
      <SettingsLayout
        title="Arrow Flight SQL OAuth Settings"
        dataSourceType="arrow_flight_sql"
        width="narrow"
      >
        <Alert
          type="error"
          showIcon
          message="Arrow Flight SQL OAuth is not supported for your domain"
          description={`Arrow Flight SQL OAuth is not supported for ${
            clientFlags?.email.split("@")[1]
          }. Please use a different account to configure Arrow Flight SQL OAuth.`}
        />
      </SettingsLayout>
    );
  }

  const handleOnFinish = (data: UpsertArrowFlightSqlAppInfoRequest) => {
    const valid = validate(data);
    if (valid) {
      upsertAppInfoMutation.mutate(form.getFieldsValue());
    }
  };

  // Show list view when apps exist and not in add mode
  if (appInfoList && appInfoList.length > 0 && !showAddNewAccountForm) {
    return (
      <SettingsLayout
        title="Arrow Flight SQL OAuth Settings"
        dataSourceType="arrow_flight_sql"
        width="narrow"
      >
        <OAuthConfiguredAlert dataSourceType="arrow_flight_sql" $marginBottom="20px" />
        <Form>
          <StyledLabel>
            <label>Accounts</label>
          </StyledLabel>
        </Form>
        {appInfoList.map((appInfo, index) => (
          <DataSourceSelections key={index}>
            <StyledItem onClick={() => null}>
              <Item.Meta
                title="Host"
                avatar={<ImportTypeIcon type={"arrow_flight_sql"} />}
                description={appInfo.host}
              />
              <StyledLink to={`${url}/${encodeURIComponent(appInfo.client_id ?? "")}`}>
                <StyledSummaryText>Manage</StyledSummaryText>
                <CaretRightBlack />
              </StyledLink>
            </StyledItem>
          </DataSourceSelections>
        ))}

        <Button
          style={{ width: 200, marginTop: 16 }}
          onClick={() => {
            setShowAddNewAccountForm(true);
            setErrorMessage("");
          }}
        >
          Add Connection
        </Button>
        <StyledModal open={showSuccessModal} onCancel={() => setShowSuccessModal(false)}>
          <StyledAlert
            showIcon
            banner
            type="success"
            message="Saved successfully"
            description="Make sure Arrow Flight SQL OAuth is working properly by clicking 'Test connection' in the Coefficient Sidebar."
            $background="white"
          />
        </StyledModal>
      </SettingsLayout>
    );
  }

  // Show add form (for new account or when no accounts exist)
  return (
    <SettingsLayout
      title="Arrow Flight SQL OAuth Settings"
      dataSourceType="arrow_flight_sql"
      width="narrow"
    >
      {errorMessage && (
        <StyledAlert
          showIcon
          type="error"
          icon={<AlertExclamation type="error" />}
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <Form
        layout="vertical"
        onFinish={handleOnFinish}
        onBeforeInput={() => setErrorMessage("")}
        initialValues={{
          host: "",
          client_id: "",
          client_secret: "",
          authorization_url: "",
          token_url: "",
          scopes: "",
        }}
        disabled={isLoading || upsertAppInfoMutation.isLoading}
        form={form}
      >
        <HelperText>
          Allows users from your domain to connect to Arrow Flight SQL using OAuth.
        </HelperText>

        <FormItem
          label="Flight SQL Host"
          name="host"
          rules={[{ required: true, message: "Host is required" }]}
        >
          <Input placeholder="flight-sql.example.com" />
        </FormItem>

        <FormItem
          label="Port"
          name="port"
          rules={[{ required: true, message: "Port is required" }]}
        >
          <Input type="number" placeholder="443" />
        </FormItem>

        <FormItem
          label="Authorization URL"
          name="authorization_url"
          rules={[{ required: true, message: "Authorization URL is required" }]}
        >
          <Input placeholder="https://idp.example.com/oauth2/authorize" />
        </FormItem>

        <FormItem
          label="Token URL"
          name="token_url"
          rules={[{ required: true, message: "Token URL is required" }]}
        >
          <Input placeholder="https://idp.example.com/oauth2/token" />
        </FormItem>

        <FormItem
          label="Client ID"
          name="client_id"
          rules={[{ required: true, message: "Client ID is required" }]}
        >
          <Input placeholder="Client ID" />
        </FormItem>
        <FormItem
          label="Client Secret"
          name="client_secret"
          rules={[{ required: true, message: "Client Secret is required" }]}
        >
          <Input type="password" placeholder="Client Secret" />
        </FormItem>

        <FormItem label="Scopes" name="scopes">
          <Input placeholder="openid profile offline_access" />
        </FormItem>

        <div style={{ marginBottom: 20 }}>
          <CallHeaderLabel>Custom Call Headers</CallHeaderLabel>
          {callHeaders.map((header, idx) => (
            <CallHeaderRow key={idx} gap={0.5}>
              <Input
                placeholder="Key"
                value={header.key}
                onChange={e => {
                  const updated = [...callHeaders];
                  updated[idx] = { ...updated[idx], key: e.target.value };
                  setCallHeaders(updated);
                }}
                style={{ flex: 1 }}
              />
              <Input
                placeholder="Value"
                value={header.value}
                onChange={e => {
                  const updated = [...callHeaders];
                  updated[idx] = { ...updated[idx], value: e.target.value };
                  setCallHeaders(updated);
                }}
                style={{ flex: 1 }}
              />
              <RemoveHeaderButton
                type="text"
                onClick={() => {
                  const updated = [...callHeaders];
                  updated.splice(idx, 1);
                  setCallHeaders(updated);
                }}
              >
                ×
              </RemoveHeaderButton>
            </CallHeaderRow>
          ))}
          <Button
            type="link"
            size="small"
            onClick={() => setCallHeaders([...callHeaders, { key: "", value: "" }])}
            style={{ padding: 0 }}
          >
            + Add header
          </Button>
        </div>

        <FlexRow gap={1}>
          <Button
            type="text"
            onClick={() => {
              if (!appInfoList || appInfoList.length === 0) {
                history.push("/dashboard/settings/oauth-settings");
              } else if (redirectOnAdd) {
                history.goBack();
              } else {
                setShowAddNewAccountForm(false);
                form.resetFields();
                setErrorMessage("");
              }
            }}
            style={{ marginLeft: 10 }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Save
          </Button>
        </FlexRow>
      </Form>
      <StyledModal open={showSuccessModal} onCancel={() => setShowSuccessModal(false)}>
        <StyledAlert
          showIcon
          banner
          type="success"
          message="Saved successfully"
          description="Make sure Arrow Flight SQL OAuth is working properly by clicking 'Test connection' in the Coefficient Sidebar."
          $background="white"
        />
      </StyledModal>
    </SettingsLayout>
  );
};
